import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, ChevronLeft, ChevronRight, Trophy, Dumbbell, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";

const TYPE_CONFIG = {
  match: { color: "#C62828", icon: Trophy, label: "Match" },
  entrainement: { color: "#1976D2", icon: Dumbbell, label: "Entraînement" },
  evenement: { color: "#FBC02D", icon: CalendarDays, label: "Événement" },
  reunion: { color: "#7B1FA2", icon: Users, label: "Réunion" },
};
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const empty = {
  title: "", type: "entrainement", start_date: "", end_date: "",
  location: "", opponent: "", team_id: "", description: "", is_home: true, result: "",
};

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/calendar").then(({ data }) => setEvents(data));
  useEffect(() => {
    load();
    api.get("/teams").then(({ data }) => setTeams(data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        team_id: form.team_id || null,
      };
      await api.post("/calendar", payload);
      toast.success("Événement créé");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error("Erreur"); }
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.delete(`/calendar/${id}`);
    load();
  };

  // Calendar grid
  const grid = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const start = new Date(first);
    const wday = (first.getDay() + 6) % 7; // Mon=0
    start.setDate(first.getDate() - wday);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = (e.start_date || "").slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingMonth = events.filter(e => {
    const d = (e.start_date || "").slice(0, 7);
    return d === `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
  });

  return (
    <div>
      <PageHeader
        overline="Planification"
        title="Calendrier"
        description="Matchs, entraînements, événements et réunions."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="create-event-btn" className="btn-club">
            <Plus className="w-4 h-4 mr-2" /> Nouvel événement
          </Button>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E0E0E0] rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">
              {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </h2>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1))} data-testid="cal-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="cal-today">Aujourd'hui</Button>
              <Button size="sm" variant="outline" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1))} data-testid="cal-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] tracking-overline text-[#616161] mb-2">
            {DAYS.map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
            {grid.map((d, i) => {
              const iso = d.toISOString().slice(0, 10);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = iso === today;
              const dayEvents = eventsByDay[iso] || [];
              return (
                <div key={i} className={`min-h-[80px] p-1.5 rounded border text-xs ${inMonth ? "bg-white" : "bg-[#F4F5F7] text-[#616161]"} ${isToday ? "border-[#C62828] ring-2 ring-[#C62828]/20" : "border-[#E0E0E0]"}`}>
                  <div className={`text-[11px] font-semibold ${isToday ? "text-[#C62828]" : ""}`}>{d.getDate()}</div>
                  <div className="space-y-0.5 mt-1">
                    {dayEvents.slice(0, 2).map(ev => {
                      const cfg = TYPE_CONFIG[ev.type];
                      return (
                        <div key={ev.id} className="text-[10px] truncate px-1 py-0.5 rounded text-white" style={{ backgroundColor: cfg.color }}>
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 2 && <div className="text-[10px] text-[#616161]">+{dayEvents.length - 2}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#E0E0E0] rounded-md p-5">
          <h3 className="font-display text-lg font-bold mb-3">Événements du mois</h3>
          {upcomingMonth.length === 0 ? (
            <p className="text-sm text-[#616161]">Aucun événement ce mois.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="event-list">
              {upcomingMonth.map(e => {
                const cfg = TYPE_CONFIG[e.type];
                const Icon = cfg.icon;
                return (
                  <div key={e.id} className="border border-[#E0E0E0] rounded p-3 text-sm" data-testid={`event-${e.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{e.title}</p>
                        <p className="text-xs text-[#616161]">{new Date(e.start_date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</p>
                        {e.location && <p className="text-xs text-[#616161] mt-0.5">📍 {e.location}</p>}
                        {e.opponent && <p className="text-xs">vs <strong>{e.opponent}</strong> {e.is_home ? "(domicile)" : "(extérieur)"}</p>}
                        {e.result && <p className="text-xs font-semibold text-[#2E7D32] mt-1">Résultat : {e.result}</p>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => onDel(e.id)}><Trash2 className="w-3.5 h-3.5 text-[#C62828]" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="event-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvel événement</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Titre *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ev-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="ev-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Équipe</Label>
                <Select value={form.team_id || "none"} onValueChange={(v) => setForm({ ...form, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date début *</Label><Input type="datetime-local" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ev-start" /></div>
              <div><Label>Date fin</Label><Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              {form.type === "match" && (
                <>
                  <div className="col-span-2"><Label>Adversaire</Label><Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} /></div>
                  <div className="col-span-2 flex items-center gap-3">
                    <Label>Domicile ?</Label>
                    <Select value={String(form.is_home)} onValueChange={(v) => setForm({ ...form, is_home: v === "true" })}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Domicile</SelectItem>
                        <SelectItem value="false">Extérieur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Résultat</Label><Input placeholder="2 - 1" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} /></div>
                </>
              )}
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="event-submit-btn">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
